import React, { Component } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { action } from "mobx";
import { observer } from "mobx-react";
import { DatePicker, LocaleProvider } from "antd";
import enUS from "antd/lib/locale-provider/en_US";
import Icon from "module-common/Icon";
import Picker from "module-common/Picker";
import PickerChannel from "module-common/PickerChannelWithQuery";
import TreePickerMultiLabel from "module-common/TreePickerMultiLabelWithQuery";
import Field from "./Field";

const filterType = {
  date: "Date",
  channel: "Channel",
  label: "Label",
  customerField: "Customer"
};
const allFilterType = [
  filterType.label,
  filterType.date,
  filterType.channel,
  filterType.customerField
];
export default observer(
  class Filter extends Component {
    static propTypes = { store: PropTypes.object.isRequired };

    render() {
      const store = this.props.store;
      // decide icon to render
      let icon;
      if (store.activeFilters.length === 1) {
        icon = <Icon name="filter" height={24} width={18} />;
      } else {
        icon = (
          <Icon
            name="refresh"
            height={24}
            width={18}
            onClick={this.clearFilter}
          />
        );
      }

      return (
        <Wrapper>
          {icon}
          <Filters>{this.renderFilters()}</Filters>
        </Wrapper>
      );
    }

    renderFilters = () => {
      const store = this.props.store;
      const filters = store.activeFilters;
      const renders = [];
      const length = filters.length;
      for (let i = 0; i < filters.length; i++) {
        const filter = filters[i];
        let buttonAdd;
        let buttonMinus = (
          <Icon
            name="minus"
            width={20}
            onClick={() => {
              this.resetFilter(filter);
              this.deleteFilter(i);
            }}
          />
        );

        // if there is only one filter then no need to show minus button
        if (i === 0 && length === 1) {
          buttonMinus = null;
        }
        // if all filter options has been shown, no need to show add button
        if (i === length - 1 && i !== 5) {
          buttonAdd = <Icon name="plus" width={20} onClick={this.addFilter} />;
        }
        renders.push(
          <Row key={i}>
            <Picker
              options={this.getUnusedFilters()}
              onSelected={row => {
                this.resetFilter(filter);
                filters[i] = row.label;
              }}
            >
              <Field text={filter} width={80} />
            </Picker>
            {this.renderPicker(filter)}
            {buttonAdd}
            {buttonMinus}
          </Row>
        );
      }
      return renders;
    };

    renderPicker = type => {
      const store = this.props.store;
      let picker;
      switch (type) {
        case filterType.channel:
          {
            const channel = store.filterChannel;
            const text = channel
              ? `@${channel.name} (${channel.channel.name})`
              : "All Channels";
            picker = (
              <PickerChannel
                onSelected={selected => (store.filterChannel = selected.value)}
              >
                <Field text={text} flex />
              </PickerChannel>
            );
          }
          break;
        case filterType.date:
          picker = (
            <LocaleProvider locale={enUS}>
              <DatePicker.RangePicker
                format="DD/MM/YYYY"
                onChange={this.onChangeDate}
              />
            </LocaleProvider>
          );
          break;
        case filterType.label:
          const labels = store.filterLabels;
          const count = labels.length;
          picker = (
            <TreePickerMultiLabel
              selectedLabels={labels.slice()}
              onSelected={this.onLabelSelected}
            >
              <Field text={`${count} labels applied`} flex />
            </TreePickerMultiLabel>
          );
          break;
        case filterType.customerField:
          picker = (
            <Form onSubmit={this.onSubmitCustomer}>
              <Input
                type="text"
                defaultValue={store.filterCustomerField}
                innerRef={input => (this.content = input)}
              />
            </Form>
          );
          break;
        default:
      }
      return picker;
    };

    onLabelSelected = selected => {
      const store = this.props.store;
      const currentLabels = store.filterLabels;
      const index = currentLabels.findIndex(
        label => label.value.id === selected.value.id
      );
      if (index !== -1) {
        currentLabels.splice(index, 1);
      } else {
        currentLabels.push(selected);
      }
      store.filterLabels = currentLabels;
    };

    onSubmitCustomer = e => {
      const store = this.props.store;
      store.filterCustomerField = this.content.value;
      this.content.blur();
      e.preventDefault();
    };

    onChangeDate = e => {
      const store = this.props.store;
      store.filterStartDate = e[0];
      store.filterEndDate = e[1];
    };

    getUnusedFilters = () => {
      const store = this.props.store;
      const unused = allFilterType.filter(
        filter => !store.activeFilters.includes(filter)
      );
      const rows = [];
      for (const row of unused) {
        rows.push({ label: row });
      }
      return rows;
    };

    resetFilter = type => {
      const store = this.props.store;
      switch (type) {
        case filterType.channel:
          store.filterChannel = null;
          break;
        case filterType.date:
          store.filterStartDate = null;
          store.filterEndDate = null;
          break;
        case filterType.label:
          store.filterLabels = [];
          break;
        case filterType.customerField:
          store.filterCustomerField = null;
          break;
        default:
      }
    };

    clearFilter = action(() => {
      const store = this.props.store;
      store.activeFilters = ["Customer"];
      store.filterLabels = [];
      store.filterStartDate = null;
      store.filterEndDate = null;
      store.filterChannel = null;
      store.filterCustomerField = null;
    });

    deleteFilter = index => {
      const store = this.props.store;
      const filters = store.activeFilters;
      const deletedFilter = filters[index];
      this.resetFilter(deletedFilter);
      filters.splice(index, 1);
    };

    addFilter = () => {
      const store = this.props.store;
      const filters = store.activeFilters;
      filters.push(this.getUnusedFilters()[0].label);
    };
  }
);

const Wrapper = styled.div`
  padding: 8px;
  flex: none;
  display: flex;
  flex-grow: 1;
`;
const Filters = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
`;
const Row = styled.div`
  display: flex;
  align-items: center;
`;
const Form = styled.form`
  display: flex;
  flex: 1;
`;
const Input = styled.input`
  flex: 1;
  margin: 2px;
  padding: 0px 4px;
  border: 1px solid ${props => props.theme.panelBorder};
  border-radius: 4px;
`;



// WEBPACK FOOTER //
// ./src/module-customer-journey/filter/FilterWithStore.js